import { useOrgSettings } from "./OrgSettingsContext";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Mail, Loader2, CheckCircle, XCircle } from "lucide-react";

export function EmailSettings() {
  const { formState, updateFormState, emailValidation } = useOrgSettings();

  return (
    <div className="space-y-6">
      {/* Outbound Email Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Outbound Email
          </CardTitle>
          <CardDescription>
            Configure the email address used for sending notifications and
            replies.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* From Email Address */}
          <div className="space-y-2">
            <Label htmlFor="fromEmailPrefix">
              From Email Address <span className="text-red-500">*</span>
            </Label>
            <div className="flex flex-col sm:flex-row">
              <div className="relative flex-1">
                <Input
                  id="fromEmailPrefix"
                  type="text"
                  value={formState.fromEmailPrefix}
                  placeholder="support"
                  className={`sm:rounded-r-none pr-8 ${
                    emailValidation.isValid === false
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : emailValidation.isValid === true
                        ? "border-green-500 focus:border-green-500 focus:ring-green-500"
                        : ""
                  }`}
                  onChange={(e) =>
                    updateFormState({ fromEmailPrefix: e.target.value })
                  }
                />
                <div className="absolute inset-y-0 right-2 flex items-center">
                  {emailValidation.isValidating && (
                    <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
                  )}
                  {!emailValidation.isValidating &&
                    emailValidation.isValid === true && (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    )}
                  {!emailValidation.isValidating &&
                    emailValidation.isValid === false && (
                      <XCircle className="h-4 w-4 text-red-500" />
                    )}
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 border sm:border-l-0 border-t-0 sm:border-t border-gray-300 dark:border-gray-600 px-3 py-2 text-sm text-gray-500 dark:text-gray-400 rounded-b-md sm:rounded-bl-none sm:rounded-r-md">
                @euserviceline.com
              </div>
            </div>
            {emailValidation.error && (
              <p className="text-sm text-red-600">{emailValidation.error}</p>
            )}
            {emailValidation.isValid === false && !emailValidation.error && (
              <p className="text-sm text-red-600">
                This email address is already in use by another organization
              </p>
            )}
            {emailValidation.isValid === true && (
              <p className="text-sm text-green-600">Email address is available</p>
            )}
          </div>

          {/* From Email Name */}
          <div className="space-y-2">
            <Label htmlFor="fromEmailName">
              From Email Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="fromEmailName"
              value={formState.fromEmailName}
              placeholder="Your Company Support"
              onChange={(e) =>
                updateFormState({ fromEmailName: e.target.value })
              }
            />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              This name will appear in the "From" field of outgoing emails.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Inbound Email Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Inbound Email
          </CardTitle>
          <CardDescription>
            Configure email-to-ticket functionality.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Enable Inbound Email */}
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1 flex-1">
              <Label htmlFor="inboundEmailEnabled" className="font-medium">
                Enable Inbound Email Processing
              </Label>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                When enabled, customers can create tickets and reply to existing
                tickets by sending emails.
              </p>
            </div>
            <Switch
              id="inboundEmailEnabled"
              checked={formState.inboundEmailEnabled}
              onCheckedChange={(checked) =>
                updateFormState({ inboundEmailEnabled: checked })
              }
            />
          </div>

          {formState.inboundEmailEnabled && (
            <>
              {/* Auto-Create Customers */}
              <div className="flex items-start justify-between gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="space-y-1 flex-1">
                  <Label htmlFor="autoCreateCustomers" className="font-medium">
                    Auto-Create Customers
                  </Label>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Automatically create customer records for unknown email
                    senders.
                  </p>
                </div>
                <Switch
                  id="autoCreateCustomers"
                  checked={formState.autoCreateCustomers}
                  onCheckedChange={(checked) =>
                    updateFormState({ autoCreateCustomers: checked })
                  }
                />
              </div>

              {/* AI Classification */}
              <div className="flex items-start justify-between gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="space-y-1 flex-1">
                  <Label
                    htmlFor="aiClassificationEnabled"
                    className="font-medium"
                  >
                    AI Ticket Classification
                  </Label>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Use AI to automatically suggest priority and category for
                    new tickets.
                  </p>
                </div>
                <Switch
                  id="aiClassificationEnabled"
                  checked={formState.aiClassificationEnabled}
                  onCheckedChange={(checked) =>
                    updateFormState({ aiClassificationEnabled: checked })
                  }
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
